import {faFolder} from "@fortawesome/pro-solid-svg-icons/faFolder";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {MailItemType} from "@twigsby/client-data";
import {
	Badge,
	ColourOutline,
	ColourSolid,
	FlexAlign,
	FlexGap,
	FlexJustify,
	Pill,
	PillSize,
	StackHorizontal,
	StackVertical,
	useTranslation
} from "@twigsby/frontend-framework";
import {CustomCheckbox} from "@twigsby/frontend-framework/lib/Components/Forms/Toggle/ToggleSwitchCustom";
import React, {memo, MutableRefObject} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMailbox} from "../../Providers/NewMailboxProvider";
import {useSite} from "../../Providers/SiteInterfaceProvider";
import {MailItemThumbnails, MailPartTypeEnum} from "./MailItemThumbnails";

interface MailGridViewItemComponentType extends MailItemType {
	selected? : boolean;
	selectable? : boolean;
	index? : string;
	callBack?: any;
	onDragStart?: any;
	onDragEnd?: any;
	onDragOver?: any;
	draggable?: boolean;
	size?: string;
	shiftKey?: MutableRefObject<boolean>;
}

const MailGridViewItem = memo((props: MailGridViewItemComponentType) => {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const { mailBoxInfo, } = useMailbox();
	const { currentLocale, setPdfViewer } = useSite();
	const TopUpRequired = props.files[0]?.topupRequired || props.files[1]?.topupRequired;

	const HandleClick = (e: React.MouseEvent) => {
		if(props.shiftKey?.current) {
			props.callBack(props.mailUuid, !props.selected);
		} else {
			navigate(`/mail/${props.clientFolder ? `custom-folder/${props.clientFolder.id}` : mailBoxInfo.folderSlug}/${props.mailUuid}`)
		}
	}

	return (
		<div
			draggable={props.draggable}
			onDragStart={(e: any) => props.onDragStart(e)}
			onDragEnd={(e: any) => props.onDragEnd(e)}
			onDragOver={(e: any) => props.onDragOver(e)}
			className={`mailbox-item-block ${props.selected ? 'item-row-selected' : ''} ${props.size ? props.size : ''} ${!props.dateRead ? 'item-row-highlighted' : ''} ${props.dateRead ? 'item-row-muted' : ''}`}
			data-item-id={props.mailUuid}
		>
			<div className={`item-row-checkbox`}
				 draggable={false}
				 onClick={(e) => e.stopPropagation()}
			>
				{props.selectable && (
					<CustomCheckbox
						onChange={(e) => props.callBack(props.mailUuid, e)}
						label={props.mailUuid}
						name={`mailItem${props.mailUuid}`}
						hideLabel={true}
						defaultChecked={props.selected}
					/>
				)}
			</div>

			<div
				onClick={(e) => HandleClick(e)}
				className={'mailbox-item-meta'}
				draggable={false}
			>
				<div className={'mail-item-thumbs-container'}>
					<div className={`thumbnail-stack`}>
						<MailItemThumbnails
							mailType={MailPartTypeEnum["Mail-Contents"]}
							files={props.files}
							onClickCallback={(link: string) => setPdfViewer(true, link)}
							hideMissing={true}
							largeThumbnail={true}
						/>
						<MailItemThumbnails
							mailType={MailPartTypeEnum['Mail-Envelopes']}
							files={props.files}
							onClickCallback={(link: string) => setPdfViewer(true, link)}
							largeThumbnail={true}
						/>
					</div>
				</div>

				<StackVertical align={FlexAlign.Center} gap={FlexGap.ExtraSmall} justify={FlexJustify.Center}  extraClass={'mail-item-reference'}>
					<StackHorizontal align={FlexAlign.Center} gap={FlexGap.ExtraSmall}>
						{!props.dateRead && <Badge colour={ColourSolid.Primary} />}

						{props.dateRead && (
							<span className='mail-item-tracking-id'>{props.trackingId}</span>
						)}

						{!props.dateRead && (
							<strong className='mail-item-tracking-id'>{props.trackingId}</strong>
						)}
					</StackHorizontal>

					{TopUpRequired && (
						<Pill content={t('mailItem.labels.topUpRequired')} size={PillSize.Small} colour={ColourOutline.Alert} />
					)}

					{props.mailLocationId !== 20 && props.mailAction.requiresUserInput && (
						<Pill content={'Item Locked'} size={PillSize.Small} colour={ColourOutline.Alert} />
					)}

					{!TopUpRequired && props.clientFolder?.parentFolder && mailBoxInfo.customFolderId !== props.clientFolder?.id && (
						<div className={`pill pill-sm flex-gap-xs ${props.clientFolder?.colour ? `${props.clientFolder?.colour}` : ColourSolid.Secondary}`}>
							<FontAwesomeIcon icon={faFolder} fixedWidth={true} />
							{props.clientFolder?.description}
						</div>
					)}

					{!TopUpRequired && props.daysUntilNextStorageCharge && (
						<Pill
							content={t('mailItem.labels.storageCharges', { number: props.daysUntilNextStorageCharge})}
							size={PillSize.Small}
							colour={props?.daysUntilNextStorageCharge && props?.daysUntilNextStorageCharge <= 5 ? ColourOutline.Alert : ColourOutline.Secondary}
						/>
					)}

					{!TopUpRequired && props.forwardingBatchId && (
						<Pill content={'View Batch'} size={PillSize.Small} colour={ColourOutline.Secondary} />
					)}
				</StackVertical>
			</div>
		</div>
	);
});

export default MailGridViewItem;
